import { PlacementXAnchor, PlacementYAnchor } from "building-types/annotation/annotationTypesEnum";
import { GeneratedDimensionPlacementType, type Dimension } from "building-types/dimensions/dimensionTypes";
import type { AutoDimensionRuntimeHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { InteriorDimensionType } from "building-types/settings/settingsTypesEnum";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { DimensionProps, SheetPolylineGeometry } from "building-types/views/sheetGeometryTypes";
import {
  GRADIENT_DESCENT_NO_CONSTRAINT,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import { line2EvalOffset } from "geom/line2";
import {
  polyLine2ByTwoPoints,
  polyLine2End,
  polyLine2IsHorizontalEdge,
  polyLine2IsVerticalEdge,
  polyLine2Start,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween } from "geom/vec2";
import { dimensionNew, getOrientedDimLine } from "../dimensions";
import type { PlanCollider } from "./interiorLabelObstacle";
import { CollisionType, planColliderObstacleNew } from "./interiorLabelObstacle";
import { dimLabelFromGroup, dimLineFromGroup } from "./planDimensionGroupTools";
import type { Obstacles } from "./planGeometrySource";
import { PlanGeometryBuilder } from "./planGeometrySource";
import type {
  GroupOfTaggedPlanGeometries,
  PlanGeometryGeneratorResult,
  TaggedPlanGeometry,
} from "./planGeometrySourceTypes";
import { normalizeTextRotation, planTextBoxPerimeter } from "./primitives/textPrimitive";
import { makePlanColliderFromSheetGeometry } from "./sheetGeometryCollision";

interface PlanDimensionProps {
  handle?: DocEntityHandle | (DocEntityHandle | undefined)[];
  dimensions: Dimension[];
  fontSize?: SheetInches;
  interiorDimensionType?: InteriorDimensionType;
  buildingEntity?: Object;
  useRoofOverhangRenderOrder?: boolean;
  witnessPlsExtensionPolyLine?: PolyLine2[][];
  planRotationRads?: number;
}

export function makeSheetGeometryFromDimensions({
  handle,
  dimensions,
  fontSize,
  buildingEntity,
  useRoofOverhangRenderOrder = false,
  witnessPlsExtensionPolyLine,
  planRotationRads = 0,
}: PlanDimensionProps): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder(true);
  const isMultiHandle = Array.isArray(handle);

  dimensions.forEach((dim, i) => {
    const {
      label,
      labelMidPtX,
      labelMidPtY,
      rangeAABB,
      textDirXOverride,
      textDirYOverride,
      placementXAnchor = PlacementXAnchor.CENTER,
      boxVerticalAlignmentPercentageOffset = 1 / 2,
      startAnchor,
      startExt,
      endAnchor,
      endExt,
      witnessLineGap,
      dimStrip,
    } = dim;

    const dimLine = getOrientedDimLine(dim.baseDimLine);
    const textDirX = textDirXOverride !== undefined ? Math.abs(textDirXOverride) : dimLine[2] - dimLine[0];
    const textDirY = textDirYOverride !== undefined ? Math.abs(textDirYOverride) : dimLine[3] - dimLine[1];
    const dimAngle = vec2AngleBetween(1, 0, textDirX, textDirY);
    const dimensionProps: DimensionProps = {
      startAnchor,
      endAnchor,
      baseDimLine: dim.baseDimLine,
      autoKind: dim.autoKind,
      startExt,
      endExt,
      witnessLineGap,
    };

    const lineTaggedGeometry: TaggedPlanGeometry = {
      category: SheetGeometryCategory.DIM_LINE,
      ...sheetPolylineGeometryNew({
        handle: isMultiHandle ? handle[i] : handle,
        paths: [dimLine],
        buildingEntity,
        dimensionProps,
      }),
    };
    const lineworkTaggedGeometry: TaggedPlanGeometry = {
      category: SheetGeometryCategory.DIM_LINEWORK,
      ...sheetPolylineGeometryNew({
        handle: isMultiHandle ? handle[i] : handle,
        paths: dim.linework,
        buildingEntity,
        dimensionProps,
      }),
    };
    const tickTaggedGeometry: TaggedPlanGeometry = {
      category: SheetGeometryCategory.DIM_TICK,
      ...sheetPolylineGeometryNew({
        handle: isMultiHandle ? handle[i] : handle,
        paths: dim.ticks,
        buildingEntity,
        dimensionProps,
      }),
    };

    const witnessPaths = dim.witnessPls.reduce((acc, witnessPl, j) => {
      const newWitnessPaths = witnessPlsExtensionPolyLine?.[j]
        ? [witnessPl, ...witnessPlsExtensionPolyLine[j]]
        : [witnessPl];
      acc.push(...newWitnessPaths);
      return acc;
    }, [] as PolyLine2[]);

    const witnessTaggedGeometry: TaggedPlanGeometry = {
      category: SheetGeometryCategory.DIM_WITNESS,
      ...sheetPolylineGeometryNew({
        handle: isMultiHandle ? handle[i] : handle,
        paths: witnessPaths,
        buildingEntity,
        dimensionProps,
      }),
    };

    let constraints: PolyLine2[] | undefined = undefined;
    let sharedConstraints: { [key: string]: PolyLine2[] | null } | undefined = undefined;
    if (dim.autoKind !== GeneratedDimensionPlacementType.INTERIOR) {
      // exterior dimensions are only allowed to move along the dim line
      constraints = [dimLine];
    } else if (rangeAABB) {
      // for now, all interior dimensions are allowed to move along the dim line
      constraints = [dimLine];
      if (!dimStrip) {
        // non-stripped interior dimensions are allowed to move along the dim range
        if (polyLine2IsHorizontalEdge(dimLine, 0)) {
          constraints.push(polyLine2ByTwoPoints(dimLine[0], rangeAABB.minY, dimLine[0], rangeAABB.maxY));
        } else if (polyLine2IsVerticalEdge(dimLine, 0)) {
          constraints.push(polyLine2ByTwoPoints(rangeAABB.minX, dimLine[1], rangeAABB.maxX, dimLine[1]));
        } else {
          // TODO: No perpendicular movement for diagonal dim lines (zero length constraint)
          constraints.push(polyLine2ByTwoPoints(dimLine[0], dimLine[1], dimLine[0], dimLine[1]));
        }
      } else {
        const sharedContstraintValues: PolyLine2[] = [];
        if (polyLine2IsHorizontalEdge(dimLine, 0)) {
          sharedContstraintValues.push(polyLine2ByTwoPoints(dimLine[0], rangeAABB.minY, dimLine[0], rangeAABB.maxY));
        } else if (polyLine2IsVerticalEdge(dimLine, 0)) {
          sharedContstraintValues.push(polyLine2ByTwoPoints(rangeAABB.minX, dimLine[1], rangeAABB.maxX, dimLine[1]));
        } else {
          const w = aabb2Width(rangeAABB);
          const h = aabb2Height(rangeAABB);

          // defensively use half the min of the aabb on either side of the line
          const offset = Math.min(w, h) / 2;

          const [sptx, spty] = line2EvalOffset(dimLine[0], dimLine[1], dimLine[2], dimLine[3], 0.5, true, offset);
          const [eptx, epty] = line2EvalOffset(dimLine[0], dimLine[1], dimLine[2], dimLine[3], 0.5, true, -offset);
          sharedContstraintValues.push(polyLine2ByTwoPoints(sptx, spty, eptx, epty));
        }
        sharedConstraints = {
          [dimStrip]: sharedContstraintValues.length > 0 ? sharedContstraintValues : GRADIENT_DESCENT_NO_CONSTRAINT,
        };
      }
    }

    const rotation = dimAngle > Math.PI / 2 ? Math.PI + dimAngle : dimAngle;
    const { flipped } = normalizeTextRotation(rotation, planRotationRads);

    const labelTaggedGeometry: TaggedPlanGeometry = {
      category: SheetGeometryCategory.DIM_LABEL,
      ...sheetTextGeometryNew({
        handle: isMultiHandle ? handle[i] : handle,
        text: label,
        fontSize,
        x: labelMidPtX,
        y: labelMidPtY,
        rotation,
        buildingEntity,
        relaxationRangeAABB: rangeAABB,
        placementXAnchor,
        placementYAnchor: PlacementYAnchor.CENTER,
        boxVerticalAlignmentPercentageOffset: boxVerticalAlignmentPercentageOffset * (flipped ? -1 : 1),
        constraints,
        sharedConstraints,
        dimensionProps,
      }),
    };

    const geometries = useRoofOverhangRenderOrder
      ? [lineTaggedGeometry, lineworkTaggedGeometry, tickTaggedGeometry, witnessTaggedGeometry, labelTaggedGeometry]
      : [lineTaggedGeometry, labelTaggedGeometry, lineworkTaggedGeometry, tickTaggedGeometry, witnessTaggedGeometry];

    builder.addGroup({ geometries }, SheetGeometryCategory.DIM_GROUP);
  });
  return builder;
}

/**
 * TODO: Delete this in favor of making relaxation functions work directly with
 * SheetGeometry and GroupOfTaggedPlanGeometries.
 */
export function makeDimensionFromSheetGeometryDimGroup(
  dimGroup: GroupOfTaggedPlanGeometries,
  autoKind?: GeneratedDimensionPlacementType,
): Dimension | undefined {
  const dimLine = dimLineFromGroup(dimGroup);
  if (!dimLine) {
    return undefined;
  }
  const dimLabel = dimLabelFromGroup(dimGroup);
  if (!dimLabel) {
    return undefined;
  }

  const dimLabelProps = dimLabel.dimensionProps;

  const dimLineWork = dimGroup.geometries.find(
    (g) => g.category === SheetGeometryCategory.DIM_LINEWORK,
  ) as SheetPolylineGeometry;
  const dimWitness = dimGroup.geometries.find(
    (g) => g.category === SheetGeometryCategory.DIM_WITNESS,
  ) as SheetPolylineGeometry;
  const dimTick = dimGroup.geometries.find(
    (g) => g.category === SheetGeometryCategory.DIM_TICK,
  ) as SheetPolylineGeometry;

  const [startX, startY] = polyLine2Start(dimLine.paths[0]);
  const [endX, endY] = polyLine2End(dimLine.paths[0]);

  return dimensionNew({
    baseDimLine: dimLine.paths[0],
    dimLine: dimLine.paths[0],
    label: dimLabel.text,
    labelUnstacked: dimLabel.text,
    linework: dimLineWork.paths,
    witnessPls: dimWitness.paths,
    ticks: dimTick.paths,
    labelMidPtX: dimLabel.x,
    labelMidPtY: dimLabel.y,
    handle: dimGroup.geometries[0].handle as AutoDimensionRuntimeHandle,
    boxVerticalAlignmentPercentageOffset: dimLabel.boxVerticalAlignmentPercentageOffset,
    autoKind,
    startAnchor: [startX, startY],
    endAnchor: [endX, endY],
    startExt: dimLabelProps?.startExt,
    endExt: dimLabelProps?.endExt,
    witnessLineGap: dimLabelProps?.witnessLineGap,
  });
}

export function dimLabelObstacles(o: Obstacles, dimGroup: GroupOfTaggedPlanGeometries) {
  const dimLabel = dimLabelFromGroup(dimGroup);
  if (!dimLabel) {
    return;
  }
  const perimeter = planTextBoxPerimeter(dimLabel);
  o.obstacles?.push(planColliderObstacleNew(CollisionType.ExteriorDimensionLabel, perimeter));
}

const DimensionTypeToCollisionType: {
  [key in InteriorDimensionType]: { collisionType: CollisionType; moveOnDimLine: boolean };
} = {
  [InteriorDimensionType.BY_STRING]: { collisionType: CollisionType.DimLabelByString, moveOnDimLine: true },
  [InteriorDimensionType.BY_ROOM_GROUP]: { collisionType: CollisionType.DimLabel, moveOnDimLine: false },
};

export function interiorDimPlanColliders(
  dimGroups: readonly GroupOfTaggedPlanGeometries[],
  dimType: InteriorDimensionType,
): PlanCollider[] {
  return dimGroups.map((g) => {
    return makePlanColliderFromSheetGeometry({
      dimSheetGeomSource: g,
      labelBorder: 1,
      mass: 1,
      ...DimensionTypeToCollisionType[dimType],
    });
  });
}

export function dimTickLineworkCategory(dim: DimensionProps): SheetGeometryCategory {
  switch (dim.autoKind) {
    case GeneratedDimensionPlacementType.EXTERIOR:
      return SheetGeometryCategory.WALL_EXTERIOR_DIM_TICK;
    case GeneratedDimensionPlacementType.INTERIOR:
      return SheetGeometryCategory.WALL_INTERIOR_DIM_TICK;
    case GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR:
    case GeneratedDimensionPlacementType.FOUNDATION_INTERIOR:
      return SheetGeometryCategory.FOUNDATION_DIM_TICK;
    case GeneratedDimensionPlacementType.UTILITY_INTERIOR:
    case GeneratedDimensionPlacementType.UTILITY_EXTERIOR:
      return SheetGeometryCategory.UTILITY_DIM_TICK;
    default:
      console.warn("dimTickLineworkCategory: unknown autoKind", dim.autoKind);
      return SheetGeometryCategory.DIM_TICK;
  }
}

export function dimLineworkCategory(dim: DimensionProps): SheetGeometryCategory {
  switch (dim.autoKind) {
    case GeneratedDimensionPlacementType.EXTERIOR:
      return SheetGeometryCategory.WALL_EXTERIOR_DIM_LINEWORK;
    case GeneratedDimensionPlacementType.INTERIOR:
      return SheetGeometryCategory.WALL_INTERIOR_DIM_LINEWORK;
    case GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR:
    case GeneratedDimensionPlacementType.FOUNDATION_INTERIOR:
      return SheetGeometryCategory.FOUNDATION_DIM_LINEWORK;
    case GeneratedDimensionPlacementType.UTILITY_INTERIOR:
    case GeneratedDimensionPlacementType.UTILITY_EXTERIOR:
      return SheetGeometryCategory.UTILITY_DIM_LINEWORK;
    default:
      console.warn("dimLineworkCategory: unknown autoKind", dim.autoKind);
      return SheetGeometryCategory.DIM_LINEWORK;
  }
}

export function dimLabelCategory(dim: DimensionProps): SheetGeometryCategory {
  switch (dim.autoKind) {
    case GeneratedDimensionPlacementType.EXTERIOR:
      return SheetGeometryCategory.WALL_EXTERIOR_DIM_LABEL;
    case GeneratedDimensionPlacementType.INTERIOR:
      return SheetGeometryCategory.WALL_INTERIOR_DIM_LABEL;
    case GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR:
    case GeneratedDimensionPlacementType.FOUNDATION_INTERIOR:
      return SheetGeometryCategory.FOUNDATION_DIM_LABEL;
    case GeneratedDimensionPlacementType.UTILITY_INTERIOR:
    case GeneratedDimensionPlacementType.UTILITY_EXTERIOR:
      return SheetGeometryCategory.UTILITY_DIM_LABEL;
    default:
      console.warn("dimLabelCategory: unknown autoKind", dim.autoKind);
      return SheetGeometryCategory.DIM_LABEL;
  }
}

export function dimWitnessCategory(dim: DimensionProps): SheetGeometryCategory {
  switch (dim.autoKind) {
    case GeneratedDimensionPlacementType.EXTERIOR:
      return SheetGeometryCategory.WALL_EXTERIOR_DIM_WITNESS;
    case GeneratedDimensionPlacementType.INTERIOR:
      return SheetGeometryCategory.WALL_INTERIOR_DIM_WITNESS;
    case GeneratedDimensionPlacementType.FOUNDATION_EXTERIOR:
    case GeneratedDimensionPlacementType.FOUNDATION_INTERIOR:
      return SheetGeometryCategory.FOUNDATION_DIM_WITNESS;
    case GeneratedDimensionPlacementType.UTILITY_INTERIOR:
    case GeneratedDimensionPlacementType.UTILITY_EXTERIOR:
      return SheetGeometryCategory.UTILITY_DIM_WITNESS;
    default:
      console.warn("dimWitnessCategory: unknown autoKind", dim.autoKind);
      return SheetGeometryCategory.DIM_WITNESS;
  }
}
